// General Imports
import React, { useEffect } from 'react'
import { Routes, Route, Link, Navigate } from 'react-router-dom'
import { Helmet } from 'react-helmet'

// Page Imports
import HomePage from './pages/HomePage'
import LoginPage from './pages/LoginPage'
import TokenPage from './pages/TokenPage'
import EditTokenPage from './pages/EditTokenPage'
import AnalyticsPage from './pages/AnalyticsPage'
import ApiBrowser from './pages/api-test-tool'
import CustomerAdminPage from './pages/CustomerAdminPage'
import CustomerEditPage from './pages/CustomerEditPage'
import CustomerDashboard from './pages/CustomerDashboard'
import ActivityLogPage from './pages/ActivityLogPage'
import EmailTemplatesPage from './pages/EmailTemplatesPage'
import PrivacyPolicyPage from './pages/PrivacyPolicyPage'
import TermsOfServicePage from './pages/TermsOfServicePage'
import TicketsPage from './pages/TicketsPage'
import SubmitTicketPage from './pages/SubmitTicketPage'
import TicketDetailPage from './pages/TicketDetailPage'
import GuidedTour from './components/GuidedTour'
import BouncingDots from './components/BouncingDots'

// Component Imports
import Navbar from './components/Navbar'
import Loader from './components/Loader'
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css'

// Hook Imports
import useAuthHook from './userAuth'
import useUserRole from './hooks/useUserRole'
import useTicketClaims from './hooks/useTicketClaims'

import { ToastProvider } from './context/ToastContext'

const App = () => {
  const { loading, user, completeEmailLinkSignIn } = useAuthHook()
  const { role, customer, loading: roleLoading } = useUserRole(user)

  // Mint ticket custom claims and force-refresh the ID token after sign-in so
  // Firestore/Storage rules and the client agree before ticket routes render.
  useTicketClaims(user)

  // Handle email link sign-in callback
  useEffect(() => {
    completeEmailLinkSignIn(window.location.href)
  }, [])

  const title = 'Inspire Health Tech Self-Service'
  const isReady = !loading && !roleLoading
  const isAdmin = isReady && role === 'admin'
  const isCustomer = isReady && role === 'customer'

  const welcomeSteps = isAdmin
    ? [
        { target: '[data-tour="nav-links"]', title: 'Navigation', content: 'Use the navigation bar to access all areas of the admin dashboard.' },
        { target: 'a[href="/tokens"]', title: 'Tokens', content: 'Create and manage API access tokens for documentation access.' },
        { target: 'a[href="/customers"]', title: 'Customers', content: 'Manage customer accounts, assign API keys, and configure access.' },
        { target: 'a[href="/api-usage"]', title: 'API Usage', content: 'View analytics and usage reports for all API keys.' },
        { target: 'a[href="/api"]', title: 'Medipro Browser', content: 'Test API endpoints interactively with different inputs.' },
        { target: 'a[href="/activity-log"]', title: 'Activity Log', content: 'Review all admin actions taken on the platform.' },
        { target: 'a[href="/settings"]', title: 'Settings', content: 'Edit the email templates used for customer invitations and token access.' }
      ]
    : isCustomer
      ? [
          { target: '[data-tour="nav-links"]', title: 'Navigation', content: 'Use the navigation bar to access your dashboard features.' },
          { target: 'a[href="/my-keys"]', title: 'My Keys', content: 'View your API keys and access tokens here.' },
          { target: 'a[href="/api-usage"]', title: 'API Usage', content: 'View your API usage analytics and reports.' },
          { target: 'a[href="/api"]', title: 'Medipro Browser', content: 'Test API endpoints interactively.' }
        ]
      : []

  // Show a full-page loader while auth is resolving
  if (!isReady && !user) {
    return (
      <ToastProvider>
        <div className='App text-xl'>
          <Helmet>
            <title>{title}</title>
          </Helmet>
          <div className='flex flex-col justify-center items-center min-h-screen gap-3'>
            <Loader />
            <p className='text-sm text-gray-500'>Loading...</p>
          </div>
        </div>
      </ToastProvider>
    )
  }

  // Show login page when auth is resolved and no user
  if (isReady && !user) {
    return (
      <ToastProvider>
        <div className='App text-xl min-h-screen flex flex-col'>
          <Helmet>
            <title>{title}</title>
          </Helmet>
          <ToastContainer position='top-right' autoClose={3000} />
          <Navbar user={null} role={null} />
          <div className='flex-grow'>
            <Routes>
              <Route path='/' element={<HomePage />} />
              <Route path='/privacy' element={<PrivacyPolicyPage />} />
              <Route path='/terms' element={<TermsOfServicePage />} />
              <Route path='*' element={<LoginPage />} />
            </Routes>
          </div>
          <footer className='text-center text-sm text-gray-500 py-4'>
            <Link to='/privacy' className='hover:underline'>Privacy Policy</Link>
            <span className='mx-2'>·</span>
            <Link to='/terms' className='hover:underline'>Terms of Service</Link>
          </footer>
        </div>
      </ToastProvider>
    )
  }

  return (
    <ToastProvider>
      <div className='App text-xl min-h-screen flex flex-col'>
        <Helmet>
          <title>{title}</title>
        </Helmet>
        <Navbar user={user} role={role} supportTicketsEnabled={customer?.supportTicketsEnabled === true} />
        <ToastContainer position='top-right' autoClose={3000} />
        {isReady && user && role && <GuidedTour steps={welcomeSteps} tourKey={`welcome_${role}`} />}
        <div className='flex-grow'>
          {!isReady && (
            <BouncingDots message='Loading your dashboard...' />
          )}
          {isReady && (
            <Routes>
              <Route exact path='/' element={<HomePage role={role} customer={customer} />} />

              {isAdmin && <Route exact path='/tokens' element={<TokenPage />} />}
              {isAdmin && <Route exact path='/tokens/:id' element={<EditTokenPage />} />}
              {isAdmin && <Route exact path='/customers' element={<CustomerAdminPage />} />}
              {isAdmin && <Route exact path='/customers/:id' element={<CustomerEditPage />} />}
              {isAdmin && <Route exact path='/activity-log' element={<ActivityLogPage />} />}
              {isAdmin && <Route exact path='/settings' element={<EmailTemplatesPage />} />}

              {isCustomer && <Route exact path='/my-keys' element={<CustomerDashboard customer={customer} />} />}

              {user && <Route exact path='/api-usage' element={<AnalyticsPage role={role} customer={customer} />} />}
              {user && <Route exact path='/api' element={<ApiBrowser role={role} customer={customer} />} />}

              {user && <Route exact path='/tickets' element={<TicketsPage role={role} customer={customer} />} />}
              {user && <Route exact path='/tickets/new' element={<SubmitTicketPage role={role} customer={customer} user={user} />} />}
              {user && <Route exact path='/tickets/:ticketId' element={<TicketDetailPage role={role} customer={customer} user={user} />} />}

              <Route path='/privacy' element={<PrivacyPolicyPage />} />
              <Route path='/terms' element={<TermsOfServicePage />} />
              <Route path='*' element={<Navigate to='/' replace />} />
            </Routes>
          )}
          {isReady && user && !role && (
            <div className='w-full m-16 text-red-600'>
              Unable to determine your access level. Please contact your administrator.
            </div>
          )}
        </div>
        <footer className='text-center text-sm text-gray-500 py-4'>
          <Link to='/privacy' className='hover:underline'>Privacy Policy</Link>
          <span className='mx-2'>·</span>
          <Link to='/terms' className='hover:underline'>Terms of Service</Link>
        </footer>
      </div>
    </ToastProvider>
  )
}

export default App
